import HorizontalRule from 'components/HorizontalRule';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import ShiftsWithNoApps from './Widgets/ShiftsWithNoApps';
import ApplicantsToReview from './Widgets/ApplicantsToReview';
import { getApplicantsEvents, getMarketplaceStats } from 'redux/ducks/Dashboard';
import './DashboardV2.scss';
import NewJobPosterDashboard from './NewJobPosterDashboard';
import MarketplaceStatsModal from './MarketplaceStatsModal';
import classNames from 'classnames';
import ShiftModifications from './Widgets/ShiftModifications';
import ATFeedback from './Widgets/ATFeedback';
import { withRouter } from 'react-router-dom';
import { get } from 'lodash';
import { trackMixpanelPageView } from 'helpers/post';
import { changeProfileField } from 'redux/ducks/Post/Profile';
import Upcoming from 'components/V2/EventList/Upcoming';
import { update } from 'redux/ducks/User';
import NetPromoterScoreModal from 'scenes/V2/NetPromoterScoreModal';
import isEnabledFeature, { features } from '@helpers/feature_flags';
import {
  triggerBlockedFailedPaymentModal,
  triggerWaitUntilFailedPaymentProcessModal,
} from 'redux/ducks/Modals';
import { customFlash, dismiss, success } from 'redux/ducks/Flash';
import {
  blockedByFailedPayment,
  pendingUnblockFailedPayment,
  showBlockedFailedPaymentPopups,
} from '@helpers/payment';
import TrackingService from 'services/TrackingService';
import { getDashboardVisitedSession, setDashboardVisitedSession } from '@helpers/sessionStorage';

export const nullOrUndefined = value => {
  return value === null || value === undefined;
};

export const failedPaymentBannerPayload = {
  type: 'error',
  message: (
    <p style={{ margin: 0 }}>
      Notice: There are failed payments on this account. Please contact{' '}
      <a href="mailto:support@go4.io">support@go4.io</a> to resolve them.
    </p>
  ),
  showDismiss: false,
};

const HELP_ARTICLES = [
  { label: 'How does the hiring process work?' },
  { label: 'How do I post a job on Go4?' },
  { label: 'Do I need to send ATs a 1099?' },
];

// Maps user.defaultEventSettingId (org-type id from ORGANIZATION_TYPES in helpers/enums)
// to the CASE_STUDIES key. performing_arts (id 2) intentionally has no case study.
const SETTING_ID_TO_ORG_TYPE = {
  0: 'industrial',
  1: 'medical',
  2: 'performing_arts',
  3: 'school',
  4: 'sports',
  5: 'facility',
};

const CASE_STUDIES = {
  school: {
    title: "When the Sideline Can't Go Empty",
    url: 'https://www.go4.io/when-the-sideline-cant-go-empty/',
  },
  sports: {
    title: 'Every Whistle, Every Weekend',
    url: 'https://www.go4.io/every-whistle-every-weekend/',
  },
  facility: {
    title: 'When the Building Never Stops',
    url: 'https://www.go4.io/when-the-building-never-stops/',
  },
  medical: {
    title: 'The Sideline Is the Front Door to Care',
    url: 'https://www.go4.io/athletic-trainers-for-hospitals-and-clinics/',
  },
  industrial: {
    title: 'Athletic Trainers on the Factory Floor',
    url: 'https://www.go4.io/athletic-trainers-on-the-factory-floor/',
  },
};

class DashboardV2 extends Component {
  state = {
    page: '',
    showAlert: {
      upcomingJobs: false,
      applicantsToReview: true,
      reviews: true,
    },
    statsModalOpen: false,
  };

  openStatsModal = () => this.setState({ statsModalOpen: true });
  closeStatsModal = () => this.setState({ statsModalOpen: false });

  componentDidMount() {
    const { user, location } = this.props;

    // Ensure marketplace stats are loaded so the dashboard can decide which
    // variant to show without flashing the wrong one. The session epic normally
    // triggers this on login/reload; this is a safety net for any nav path that
    // lands here without stats (and a retry after a failed fetch).
    const { loaded, loading } = this.props.marketplaceStats;
    if (!loaded && !loading) this.props.getMarketplaceStats();

    const urlSearchString = get(location, 'search');
    const params = new URLSearchParams(urlSearchString);
    const page = params.get('widget');
    const urlFromParam = params.get('from');
    const searchIndex = WIDGETS.findIndex(w => w.id === page);
    const from = get(location, 'state.from', '');

    this.setState({ page: searchIndex === -1 ? 'upcomingJobs' : page });

    trackMixpanelPageView('/dashboard', user);

    this.trackDashboardViewEvent();

    this.props.changeProfileField('isFirstEvent', false);
    if (showBlockedFailedPaymentPopups(user)) this.props.customFlash(failedPaymentBannerPayload);

    if (from === 'create-event-blocked-failed-payment') {
      if (pendingUnblockFailedPayment(user)) {
        this.props.toggleWaitUntilFailedPaymentProcess();
        this.props.history.replace({ ...location, state: {} });
        return;
      }

      if (blockedByFailedPayment(user)) {
        this.props.toggleBlockedForFailedPayments();
        this.props.history.replace({ ...location, state: {} });
        return;
      }
    }

    if (urlFromParam === 'request-consultation') {
      const message = 'Your request was successfully submitted.';
      this.props.success(message, true);
    }
  }

  componentWillUnmount() {
    this.props.dismissFlash();
  }

  trackDashboardViewEvent = () => {
    const { user, session } = this.props;
    if (!user.id) return;

    const previouslyDashboardVisited = getDashboardVisitedSession();

    if (previouslyDashboardVisited) return;

    const trackingService = new TrackingService(session);
    trackingService.track({
      event_name: 'dashboard_view',
      event_data: {
        jp_id: user.id,
        jp_email: user.email,
        jp_first_name: user.firstName,
        jp_last_name: user.lastName,
      },
    });
    const dashboardVisited = true;

    setDashboardVisitedSession(dashboardVisited);
  };

  onClickWidget = id => {
    this.setState({
      showAlert: { ...this.state.showAlert, [id]: false },
      page: id,
    });
  };

  isActiveWidget = id => {
    const widgetStatus = {
      applicantsToReview: this.props.totalPendingApplicants > 0,
      upcomingJobs: false,
      reviews: this.props.reviewsCount > 0,
      noAppsShifts: this.props.shiftsWithNoAppsCount > 0,
      modifications: this.props.modificationsCount > 0,
    };

    return widgetStatus[id] || false;
  };

  render() {
    const { user } = this.props;
    const { loaded, zipError, hasEvents: statsHasEvents } = this.props.marketplaceStats;

    // Until marketplace stats resolve we can't reliably tell whether the operator
    // has events: user.jobsListed is a denormalized value that lags right after
    // posting, so trusting it here flashes the wrong dashboard for a moment. Show
    // a brief loading state instead, and only fall back to jobsListed if the
    // stats fetch actually failed (so we never spin forever).
    if (!loaded && !zipError) {
      return (
        <div className="dashboardV2">
          <div className="dashboard-loading">
            <div className="dashboard-loading-spinner" />
          </div>
        </div>
      );
    }

    const orgType = SETTING_ID_TO_ORG_TYPE[user.defaultEventSettingId];
    const caseStudy = CASE_STUDIES[orgType];
    const isOpenTCModal = !user.termsAndConditions;
    const isOpenNPSModal =
      isEnabledFeature(features.NET_PROMOTER_SCORE, user) &&
      !isOpenTCModal &&
      nullOrUndefined(user.NPSScore) &&
      user.NPSAdditionalFeedback !== 'NPS unanswered, popup closed';

    const hasEvents = loaded ? statsHasEvents : !!user.jobsListed;

    if (!hasEvents) {
      return (
        <div className="dashboardV2">
          <NewJobPosterDashboard />
          <NetPromoterScoreModal isOpen={isOpenNPSModal} updateUser={this.props.update} user={user} />
        </div>
      );
    }

    return (
      <div className="dashboardV2">
        <div className="header">
          <div className="col-md-12">
            <h1>Dashboard</h1>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12">
            <div className="widgets">
              {WIDGETS.map(({ id, title, tooltip }) => {
                return (
                  <div
                    key={id}
                    id={id}
                    className={classNames('widget', 'col-md-3', {
                      active: this.state.page === id,
                    })}
                    onClick={() => this.onClickWidget(id)}
                  >
                    <div className="widget-title">{title}</div>
                    <HorizontalRule dashed margin="5px" />
                    {this.state.page === id && id !== 'upcomingJobs' ? (
                      <div className="widget-tooltip">{tooltip}</div>
                    ) : (
                      <div className="widget-count">
                        {id === 'noAppsShifts' && String(this.props.shiftsWithNoAppsCount).padStart(3, '0')}
                        {id === 'upcomingJobs' && String(this.props.upcomingJobsCount).padStart(3, '0')}
                        {id === 'applicantsToReview' &&
                          String(this.props.totalPendingApplicants).padStart(3, '0')}
                        {id === 'reviews' && String(this.props.reviewsCount).padStart(3, '0')}
                        {id === 'modifications' && String(this.props.modificationsCount).padStart(3, '0')}
                      </div>
                    )}

                    {this.isActiveWidget(id) && <div className="widget-alert">!</div>}
                  </div>
                );
              })}
            </div>
            {this.state.page === 'noAppsShifts' && (
              <ShiftsWithNoApps {...WIDGETS.find(({ id }) => id === 'noAppsShifts')} />
            )}
            {this.state.page === 'upcomingJobs' && (
              <Upcoming from="dashboard" {...WIDGETS.find(({ id }) => id === 'upcomingJobs')} />
            )}
            {this.state.page === 'applicantsToReview' && (
              <ApplicantsToReview {...WIDGETS.find(({ id }) => id === 'applicantsToReview')} />
            )}
            {this.state.page === 'reviews' && <ATFeedback {...WIDGETS.find(({ id }) => id === 'reviews')} />}
            {this.state.page === 'modifications' && (
              <ShiftModifications {...WIDGETS.find(({ id }) => id === 'modifications')} />
            )}
          </div>
        </div>
        {/* AT Area banner for existing users */}
        <div
          className="njp-existing-at-banner"
          style={{
            border: '2px solid #555',
            padding: '14px 20px',
            marginTop: '20px',
            cursor: 'pointer',
            textAlign: 'center',
          }}
          onClick={this.openStatsModal}
        >
          To see how many athletic trainers are in your area,{' '}
          <span style={{ color: '#1a7f7a', textDecoration: 'underline' }}>click here</span>.
        </div>

        {/* Bottom row: help articles + case study */}
        <div className="njp-bottom-row" style={{ display: 'flex', gap: '20px', marginTop: '20px' }}>
          <div className="njp-help-articles" style={{ flex: '0 0 35%', maxWidth: '35%' }}>
            <div
              className="njp-section-header"
              style={{ backgroundColor: '#555', color: 'white', padding: '10px 14px', fontWeight: 'bold' }}
            >
              TOP HELP ARTICLES
            </div>
            <div style={{ border: '1px solid #ccc', borderTop: 'none', padding: '10px 14px' }}>
              {HELP_ARTICLES.map(({ label }, i) => (
                <p
                  key={i}
                  style={{
                    color: '#1a7f7a',
                    fontSize: '13px',
                    cursor: 'pointer',
                    marginBottom: i < HELP_ARTICLES.length - 1 ? '8px' : '0',
                  }}
                >
                  {label}
                </p>
              ))}
            </div>
          </div>
          <div className="njp-case-study" style={{ flex: 1, padding: '16px', backgroundColor: '#f5f5f5' }}>
            <p style={{ fontSize: '13px', color: '#555', marginBottom: '4px' }}>
              [{orgType ? orgType.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()) : 'JP Type'}] Case Study
            </p>
            {caseStudy && (
              <>
                <p style={{ fontSize: '16px', fontWeight: 'bold', color: '#333', marginBottom: '4px' }}>
                  {caseStudy.title}
                </p>
                <div style={{ width: '30px', borderBottom: '3px solid #e63333', marginBlock: '6px' }} />
                <a
                  href={caseStudy.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1a7f7a', fontSize: '13px' }}
                >
                  Read More...
                </a>
              </>
            )}
          </div>
        </div>

        <MarketplaceStatsModal isOpen={this.state.statsModalOpen} onClose={this.closeStatsModal} />
        <NetPromoterScoreModal isOpen={isOpenNPSModal} updateUser={this.props.update} user={user} />
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    reviewsCount: state.dashboard.reviews.pagination.total,
    shiftsWithNoAppsCount: state.dashboard.shifts.pagination.total,
    modificationsCount: state.dashboard.modifications.pagination.total,
    totalPendingApplicants: state.dashboard.applicants.totalPendingApplicants || 0,
    upcomingJobsCount: state.event.events.upcomingPagination.total || 0,
    marketplaceStats: state.dashboard.marketplaceStats,
    user: state.session.currentUser,
    session: state.session,
  };
};

const mapDispatchToProps = dispatch => ({
  getApplicantsEvents: () => dispatch(getApplicantsEvents()),
  getMarketplaceStats: () => dispatch(getMarketplaceStats()),
  changeProfileField: (name, value) => dispatch(changeProfileField({ name, value })),
  update: user => dispatch(update(user)),
  toggleBlockedForFailedPayments: () => dispatch(triggerBlockedFailedPaymentModal()),
  toggleWaitUntilFailedPaymentProcess: () => dispatch(triggerWaitUntilFailedPaymentProcessModal()),
  customFlash: args => dispatch(customFlash(args)),
  dismissFlash: () => dispatch(dismiss({ showBlockedFailedPayment: true })),
  success: (message, scrollIntoView) => dispatch(success(message, scrollIntoView)),
});

const WIDGETS = [
  {
    id: 'upcomingJobs',
    title: 'Upcoming Jobs',
    tooltip: '',
    text: 'Upcoming Jobs',
  },
  {
    id: 'applicantsToReview',
    title: 'Applicants to Review',
    tooltip:
      'Unanswered applications expire after 7 days. Once an AT is accepted, they have 24 hrs to confirm the job.',
    text: 'Respond to applicants within 7 days',
  },
  {
    id: 'modifications',
    title: 'Shift Modifications',
    tooltip:
      "ATs can modify the times of a completed shift. You have 24hrs to review before it's auto-accepted.",
    text: 'Review within 24 Hrs',
  },
  {
    id: 'reviews',
    title: 'Provide AT Feedback',
    tooltip:
      'Provide feedback on ATs that recently worked for you. Go4 will not share this feedback with the AT.',
    text: 'Submit your feedback',
  },
];

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DashboardV2));
